<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded && idea">
      <ontos-page-header v-bind="heading">
        <!-- Excluir age sobre a coleção inteira, e fica no cabeçalho da página. O Editar, que muda só o
             que a aba Informações mostra, mora nela (pedido do usuário) — como no design do ambiente. -->
        <template #actions>
          <ontos-header-action v-if="can('designer')" icon="mdi-delete" :label="$tt('delete')"
                               color="error" @click="deleteClass()"/>
        </template>
        <!-- Os controles que só o host conhece, fora do bloco: fechar e abrir em página inteira
             no modal -->
        <template v-if="$slots.controls" #controls>
          <slot name="controls"/>
        </template>
      </ontos-page-header>

      <!-- As partes da coleção em abas, e não empilhadas: o mesmo arranjo do design do ambiente, pelo
           mesmo TabbedSection (pedido do usuário). Na rota a aba vai para a URL; na janela, não — ali a
           URL é a da tela de trás. -->
      <ontos-tabbed-section :tabs="tabs" :opens="tabOpens" :routed="!ideaAliasProp">
        <!-- O botão principal de cada aba -->
        <template #controls="{tab}">
          <ontos-header-action v-if="tab === 'info' && can('designer')" icon="mdi-pencil" color="primary"
                               :label="$tt('edit')" @click="classOnEdit = true"/>
          <!-- Um botão só: a espécie (atributo, relacionamento ou espelho) é escolhida
               dentro do modal. Antes eram dois botões fixos mais UM POR relacionamento
               de entrada de todo o workspace, o que crescia sem limite na barra. -->
          <ontos-header-action v-else-if="tab === 'properties' && can('designer')" icon="mdi-plus"
                               color="primary" :label="$tt('property-new')" @click="openCreate()"/>
          <ontos-header-action v-else-if="tab === 'workflow' && can('designer')" icon="mdi-plus"
                               color="primary" :label="$tt('workflow-create-short')" @click="openWorkflowCreate()"/>
          <ontos-header-action v-else-if="tab === 'access' && can('designer')" icon="mdi-pencil"
                               color="primary" :label="$tt('access.edit')" @click="accessOnEdit = true"/>
        </template>

        <template #default="{tab}">
          <ontos-info-row v-if="tab === 'info'">
            <ontos-view-info :title="$tt('collection-name-singular')">{{ idea.name }}</ontos-view-info>
            <ontos-view-info :title="$tt('collection-name-plural')">{{ idea.pluralName }}</ontos-view-info>
            <ontos-view-info :title="$tt('alias')"><span class="ontos-code">{{ idea.alias }}</span></ontos-view-info>
          </ontos-info-row>

          <!-- Duas caixas de soltar (pedido do usuário): no alto a do identificador, com o nome dos
               itens montado na ordem; embaixo as demais. Arrastar de uma para a outra põe ou tira a
               propriedade do identificador, e a ordem de cima para baixo é a dos campos no item.
               Listas, e não a grade de cards de antes: numa grade que quebra linha a ordem se lia em
               zigue-zague. O item assenta ao ser solto, como no Kanban (`settlingDrag`). -->
          <div v-else-if="tab === 'properties'" class="property-zones">
            <section v-for="zone of propertyZones" :key="zone.key" class="property-zone"
                     :class="zone.classes">
              <header class="property-zone-header">
                <v-icon v-if="zone.icon" size="small" color="primary">{{ zone.icon }}</v-icon>
                <span class="property-zone-title">{{ zone.title }}</span>
                <span v-if="zone.subtitle" class="text-body-2 text-medium-emphasis">{{ zone.subtitle }}</span>
                <!-- Um "+" por caixa, como o das raias do Kanban (pedido do usuário): a propriedade nasce
                     nela — a do identificador, já marcada para compô-lo -->
                <v-btn v-if="can('designer')" class="property-zone-create" variant="text" icon size="small"
                       :title="zone.createLabel" @click="openCreate(zone.key === 'identifier')">
                  <v-icon size="small">mdi-plus</v-icon>
                </v-btn>
              </header>
              <p v-if="zone.notice" class="property-zone-notice text-body-2" :class="zone.noticeClass">{{ zone.notice }}</p>
              <draggable v-model="layout[zone.key]" v-bind="propertyDrag" :data-zone="zone.key" class="property-list"
                         @start="onDragStart(zone.key, $event)" @end="onDrop($event)">
                <template #item="{element: property}">
                  <ontos-property-row :property="property" :names="names" :can-write="can('designer')"
                                      @edit="openEdit($event)" @delete="deletingProperty = $event"/>
                </template>
              </draggable>
            </section>
          </div>

          <div v-else-if="tab === 'workflow'">
            <!-- Um seletor em vez de desassociar e associar em dois passos. Limpar o campo
                 desassocia. Sem fluxo nenhum no ambiente a seção continuava existindo mas
                 sumia inteira, sem dizer por quê nem oferecer criar. -->
            <v-row no-gutters>
              <v-col cols="12" sm="6" md="4">
                <v-select v-if="workspace.workflows.length"
                          :items="workspace.workflows" item-title="name" item-value="alias"
                          :model-value="idea.workflow || null" :label="$tt('workflow')"
                          :placeholder="$tt('workflow-none')" persistent-placeholder
                          clearable density="compact" variant="outlined" @update:model-value="onWorkflowChange">
                  <!-- Configurar o fluxo escolhido, colado ao campo que o escolhe (pedido do usuário) —
                       antes era um botão do cabeçalho, longe do fluxo de que falava. No `append`, por
                       fora da caixa, e não no `append-inner`: lá dentro ele dividiria o lugar com o X
                       de limpar e a seta, e o clique tenderia a abrir a lista. -->
                  <template v-if="can('designer') && idea.workflow && workflow" #append>
                    <v-btn icon="mdi-cog" variant="text" size="small" density="comfortable"
                           :title="$tt('workflow-design-named', {workflow: workflow.name})"
                           :aria-label="$tt('workflow-design-named', {workflow: workflow.name})"
                           @click="workflowOnEdit = workflow"/>
                  </template>
                </v-select>
                <p v-else class="text-body-2 text-grey">{{ $tt('workflow-none-in-workspace') }}</p>
              </v-col>
            </v-row>

            <template v-if="idea.workflow && workflow">
              <ontos-info-row class="mb-2">
                <ontos-view-info :title="$tt('alias')"><span class="ontos-code">{{ workflow.alias }}</span></ontos-view-info>
              </ontos-info-row>
              <ontos-state-list :states="workflow.states" :title="$tt('states')" :level="4" dense :divider="false"/>
            </template>
          </div>

          <!-- As quatro perguntas sempre à vista, com a fórmula ou "Sempre": quem desenha vê de relance
               quem pode o quê, sem abrir o editor — que antes ficava escondido no "editar coleção". -->
          <div v-else>
            <ontos-view-info v-for="kind of accessKinds" :key="kind" :title="$tt(accessLabels[kind])">
              <code v-if="idea.access && idea.access[kind]" class="access-formula ontos-code">{{ idea.access[kind] }}</code>
              <span v-else class="text-medium-emphasis">{{ $tt('access.everyone') }}</span>
            </ontos-view-info>
          </div>
        </template>
      </ontos-tabbed-section>

      <!-- Entrar no identificador exige ser obrigatório: quem arrastou decide (escolha do usuário) -->
      <ontos-ask-dialog v-if="pendingLayout" :title="$tt('identifier-required-title')"
                        :message="$tt('identifier-required-confirm', {property: pendingLayout.entering.name})"
                        :actions="requiredActions"
                        @pick="$event === 'confirm' ? saveLayout(pendingLayout.layout) : cancelLayout()"
                        @closed="cancelLayout()"/>

      <!-- A pergunta de excluir do item — o título, a frase e a largura dela (relato do usuário: "fora
           de padrão") —, e não o `confirm` do navegador. O nome em negrito vem da frase traduzida -->
      <ontos-ask-dialog v-if="deletingProperty" max-width="620px"
                        :title="$tt('delete.title', {collection: $tt('property')})"
                        :actions="deleteActions()"
                        @pick="onDeletePicked($event)"
                        @closed="deletingProperty = null">
        <p v-html="$tt('delete.message', {name: deletingProperty.name})"></p>
      </ontos-ask-dialog>

      <ontos-property-dialog v-if="dialogOpen"
                             :idea="idea" :workspace="workspace" :property="propertyOnEdit"
                             :identifier="creatingInIdentifier"
                             :saving="savingProperty"
                             @save="saveDialogProperty($event)"
                             @closed="closeDialog()"/>

      <ontos-class-dialog v-if="classOnEdit" ref="classDialog"
                          :idea="idea" :workspace="workspace" :saving="savingClass"
                          @save="saveClass($event)"
                          @closed="classOnEdit = false"/>

      <ontos-access-dialog v-if="accessOnEdit"
                           :idea="idea" :workspace="workspace" :saving="savingAccess"
                           @save="saveAccess($event)"
                           @closed="accessOnEdit = false"/>

      <!-- workflowOnEdit: false = fechado, true = criando, o fluxo = editando aquele -->
      <ontos-workflow-dialog v-if="workflowOnEdit"
                             :workflow="workflowOnEdit === true ? undefined : workflowOnEdit"
                             :workspace="workspace" :saving="savingWorkflow"
                             @create="createWorkflowAndUse($event)"
                             @save="saveWorkflow($event)"
                             @closed="workflowOnEdit = false"/>
    </div>
  </ontos-base>
</template>
<script lang="ts">
import {Component, Prop, Watch, toNative} from 'vue-facing-decorator'
import OntosViewInfo from "@/components/shared/ViewInfo.vue";
import {DependencyError, NotFoundError, NotUniqueError} from "@/errors/errors";
import IdeaProperty from "@/types/IdeaProperty";
import {PropertySave} from "@/types/PropertySave";
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import PropertyRow from "@/components/design/class/PropertyRow.vue";
import AskDialog, {Ask, deleteActions} from "@/components/shared/AskDialog.vue";
import ClassDialog from "@/components/design/class/ClassDialog.vue";
import AccessDialog from "@/components/design/class/AccessDialog.vue";
import {ACCESS_KINDS, ACCESS_LABELS, IdeaAccess} from "@/types/Idea";
import PropertyDialog from "@/components/design/property/PropertyDialog.vue";
import WorkflowDialog from "@/components/design/workflow/WorkflowDialog.vue";
import StateList from "@/components/design/workflow/StateList.vue";
import InfoRow from "@/components/shared/InfoRow.vue";
import TabbedSection from "@/components/shared/TabbedSection.vue";
import {ViewTab} from "@/components/shared/ViewTabs.vue";
import {COLLECTION_DESIGN_OPENS, COLLECTION_DESIGN_TABS, workspaceDesignRoute} from "@/utils/breadcrumb";
import {identifierRefusalText, NameResolver} from "@/utils/propertyDefinition";
import {
  enteringNotRequired, identifierDropBlocker, identifierPreview, layoutChanged, layoutOf, layoutRequest,
  PropertyLayout, propertiesOf
} from "@/utils/propertyLayout";
import {workspaceNames} from "@/utils/workspaceNames";
import {collectionDesign} from "@/utils/heading";
import {refuse, showRefusal} from "@/utils/refusal";
import {citingFormulasMessage} from "@/utils/domainErrors";
import draggable from "vuedraggable";
import {settleDrop, settlingDrag} from "@/utils/motion";

/**
 * O rótulo e o ícone de cada aba (ícone pedido pelo usuário). O fluxo com o nome curto, só aqui: aba
 * pede palavra curta. Os ícones são o do fluxo no diagrama e os que as seções tinham antes das abas.
 */
const TABS = {
  info: {label: 'info-tab', icon: 'mdi-information-outline'},
  properties: {label: 'properties', icon: 'mdi-view-column'},
  workflow: {label: 'workflow-short', icon: 'mdi-sync'},
  access: {label: 'access.title', icon: 'mdi-shield-account'}
} as const

@Component({
  components: {
    OntosViewInfo,
    'ontos-info-row': InfoRow,
    'ontos-tabbed-section': TabbedSection,
    'ontos-property-row': PropertyRow,
    'ontos-ask-dialog': AskDialog,
    'ontos-class-dialog': ClassDialog,
    'ontos-access-dialog': AccessDialog,
    'ontos-property-dialog': PropertyDialog,
    'ontos-workflow-dialog': WorkflowDialog,
    'ontos-state-list': StateList,
    draggable
  }
})
class ViewClass extends IdeaLevelRouteComponent {
  /** Quando vem preenchido, a tela não é dirigida pela URL: está dentro de um modal */
  @Prop({type: String, default: ''}) readonly ideaAliasProp!: string

  //Sem chamar super: o getter cru do protótipo fura o cache de computed do Vue
  get ideaAlias(): string {
    return this.ideaAliasProp || this.params.idea
  }

  /** Params de rota para os links, com o alias certo mesmo dentro do modal */
  get routeParams() {
    return {...this.params, idea: this.ideaAlias}
  }

  get heading() {
    return collectionDesign(this.idea)
  }

  get tabs(): ViewTab[] {
    return COLLECTION_DESIGN_TABS.map(value => ({value, icon: TABS[value].icon, title: this.$tt(TABS[value].label) as string}))
  }

  tabOpens = COLLECTION_DESIGN_OPENS

  /** As duas caixas como a tela as mostra — o arrasto mexe aqui; refeitas da coleção a cada mudança dela. */
  layout: PropertyLayout = {identifier: [], others: []}
  /** A propriedade em arrasto: a caixa do identificador diz, já, se a aceita. */
  dragged: IdeaProperty | null = null
  /** O arrasto que espera a pergunta do obrigatório: o arranjo novo, e quem entrou sem ser. */
  pendingLayout: { layout: PropertyLayout, entering: IdeaProperty } | null = null

  @Watch('idea.properties', {immediate: true})
  onPropertiesChanged() {
    this.layout = layoutOf((this.idea && this.idea.properties) || [])
  }

  /**
   * As opções das duas listas: o mesmo grupo (arrasta-se de uma para a outra), pela alça, com o
   * assentar do Kanban, e a recusa da caixa do identificador ao que não pode entrar (`move`).
   */
  get propertyDrag() {
    return {
      ...settlingDrag, group: 'properties', itemKey: 'alias', handle: '.ontos-grip',
      ghostClass: 'ontos-drop-ghost', emptyInsertThreshold: 24, disabled: !this.can('designer'),
      move: (event: {to: HTMLElement, draggedContext: {element: IdeaProperty}}) =>
          event.to.dataset.zone !== 'identifier' || !identifierDropBlocker(event.draggedContext.element)
    }
  }

  /** Por que a caixa do identificador recusa o que se arrasta — a frase dita nela, enquanto se arrasta. */
  get draggedRefusal(): string {
    const blocker = this.dragged && !this.dragged.identifier ? identifierDropBlocker(this.dragged) : null
    return blocker ? identifierRefusalText(this.dragged!.name, blocker, this.dragged!.nameBlocker) : ''
  }

  /** As duas caixas: o título, o que se diz nelas e a marca. */
  get propertyZones() {
    const identifier = this.layout.identifier
    return [{
      key: 'identifier' as const,
      icon: 'mdi-key',
      title: this.$tt('identifier-zone'),
      //O nome dos itens montado na ordem, sem rótulo: ao lado de "Nome dos itens", ele já se explica — e
      //também com a caixa vazia, com o nome que o servidor dá (pedido do usuário)
      subtitle: `«${identifierPreview(this.layout, this.idea.name)}»`,
      notice: this.draggedRefusal || (identifier.length ? '' : this.$tt('identifier-zone-empty')),
      noticeClass: this.draggedRefusal ? 'text-error' : 'text-medium-emphasis',
      classes: {'property-zone--identifier': true, 'property-zone--refusing': !!this.draggedRefusal},
      createLabel: this.$tt('property-new-identifier')
    }, {
      key: 'others' as const,
      icon: '',
      title: this.$tt('other-properties'),
      subtitle: '',
      notice: '',
      noticeClass: '',
      classes: {},
      createLabel: this.$tt('property-new')
    }]
  }

  /** A propriedade cuja exclusão se está perguntando. */
  deletingProperty: IdeaProperty | null = null
  //Exposta ao template: as saídas de toda exclusão
  deleteActions = deleteActions

  get requiredActions(): Ask[] {
    return [
      {value: '', label: this.$tt('cancel') as string, text: true},
      {value: 'confirm', label: this.$tt('identifier-make-required') as string, icon: 'mdi-key', color: 'primary'}
    ]
  }

  dialogOpen = false
  classOnEdit = false
  savingClass = false
  accessOnEdit = false
  savingAccess = false
  accessKinds = ACCESS_KINDS
  accessLabels = ACCESS_LABELS
  /** false = fechado; true = criando; o fluxo = editando aquele */
  workflowOnEdit: any = false
  /** undefined = criando; objeto = editando aquela propriedade */
  propertyOnEdit: IdeaProperty | undefined = undefined
  /** Criando pelo "+" da caixa do identificador. */
  creatingInIdentifier = false
  savingProperty = false
  savingWorkflow = false

  get names(): NameResolver {
    return workspaceNames(this.workspace)
  }

  /** `inIdentifier`: pelo "+" da caixa do identificador, a propriedade nasce marcada para compô-lo. */
  openCreate(inIdentifier = false) {
    this.propertyOnEdit = undefined
    this.creatingInIdentifier = inIdentifier
    this.dialogOpen = true
  }

  openEdit(property: IdeaProperty) {
    this.propertyOnEdit = property
    this.dialogOpen = true
  }

  closeDialog() {
    this.dialogOpen = false
    this.propertyOnEdit = undefined
  }

  async saveDialogProperty(save: PropertySave) {
    this.savingProperty = true
    try {
      if (await this.savePropertyReporting(save, this.idea)) this.closeDialog()
    } finally {
      this.savingProperty = false
    }
  }

  onDragStart(zone: keyof PropertyLayout, event: {oldIndex: number}) {
    this.dragged = this.layout[zone][event.oldIndex]
  }

  /**
   * Soltou: o item assenta, e o arranjo novo grava — como o arrasto do Kanban. Soltar onde estava não
   * vai ao servidor. Quem entrou no identificador sem ser obrigatório espera a pergunta.
   */
  onDrop(event: {to: HTMLElement, newIndex: number}) {
    settleDrop(event)
    this.dragged = null
    if (!layoutChanged(this.layout, this.idea.properties)) return
    const entering = enteringNotRequired(this.layout)
    if (entering) this.pendingLayout = {layout: this.layout, entering}
    else this.saveLayout(this.layout)
  }

  /** Desistiu de tornar obrigatório: as caixas voltam a ser o que a coleção tem. */
  cancelLayout() {
    this.pendingLayout = null
    this.onPropertiesChanged()
  }

  /**
   * Grava a ordem e quem compõe o identificador (`propertyOrder` e `identifiers`) — a ordem é a dos
   * campos no item, e o identificador é o nome deles (pedido do usuário).
   *
   * Otimista, e sem o "salvando" que cobre a tela: as propriedades novas vão já para a coleção do
   * ambiente — o formulário, a ficha e a grade as leem de lá — e, se o servidor recusar, volta o que
   * havia, com a recusa na janela. Nada a reler: no servidor mudam só a ordem e as marcas (ver o
   * `IdeaRepository.reorderProperties`).
   */
  async saveLayout(layout: PropertyLayout) {
    this.pendingLayout = null
    const before = this.idea.properties
    this.idea.properties = propertiesOf(layout)
    try {
      await this.$ideas.update({systemAlias: this.workspace.alias, ideaAlias: this.idea.alias}, layoutRequest(layout))
      this.info(this.$tt('info.property-order-saved'))
    } catch (err) {
      this.idea.properties = before
      refuse(err, this.idea, {fallback: this.$tt('error.property-order')})
    }
  }

  async onDeletePicked(action: string) {
    const property = this.deletingProperty!
    this.deletingProperty = null
    if (action === 'delete') await this.deleteProperty(property)
  }

  async deleteProperty(property: IdeaProperty) {
    try {
      await this.deletePropertyFromIdea(this.idea, property)
      this.info(this.$tt('info.property-deleted', {property: property.name, collection: this.idea.name}))
    } catch (err) {
      this.throwError(this.$tt('error.delete-property', {name: property.name}), err)
    }
  }

  async saveClass(changes: any) {
    this.savingClass = true
    try {
      await this.updateCurrentIdea(changes)
      this.info(this.$tt('info.collection-updated', {name: this.idea.name}))
      this.classOnEdit = false
    } catch (err) {
      //Colisão de nome volta para o campo que colidiu, e não para a barra de erro
      if (err instanceof NotUniqueError) {
        (this.$refs.classDialog as any).notUnique(err.data.property)
      } else {
        refuse(err, this.idea, {fallback: this.$tt('error.update-collection', {name: this.idea.name})})
      }
    } finally {
      this.savingClass = false
    }
  }

  async saveAccess(access: IdeaAccess) {
    this.savingAccess = true
    try {
      await this.updateCurrentIdea({access})
      this.info(this.$tt('info.collection-updated', {name: this.idea.name}))
      this.accessOnEdit = false
    } catch (err) {
      //A fórmula que o servidor recusa ao gravar (uma roda entre coleções, que o editor de uma
      //pergunta só não enxerga) tem frase própria
      refuse(err, this.idea, {fallback: this.$tt('error.update-collection', {name: this.idea.name})})
    } finally {
      this.savingAccess = false
    }
  }

  openWorkflowCreate() {
    this.workflowOnEdit = true
  }

  /**
   * Criar o fluxo a partir da coleção só faz sentido para usá-lo: ele já nasce
   * associado, em vez de exigir um segundo passo no seletor logo ao lado.
   */
  async createWorkflowAndUse(workflow: any) {
    this.savingWorkflow = true
    try {
      await this.createWorkflow(workflow)
      this.info(this.$tt('info.workflow-created', {name: workflow.name}))
      await this.updateCurrentIdea({workflow: workflow.alias})
      this.workflowOnEdit = false
    } catch (err) {
      refuse(err, this.idea, {fallback: this.$tt('error.create-workflow', {name: workflow.name})})
    } finally {
      this.savingWorkflow = false
    }
  }

  async saveWorkflow(changes: any) {
    const workflow = this.workflow!
    this.savingWorkflow = true
    try {
      await this.updateWorkflow(workflow, changes)
      this.info(this.$tt('info.workflow-updated', {name: workflow.name}))
      this.workflowOnEdit = false
    } catch (err) {
      showRefusal(citingFormulasMessage(err) || this.$tt('error.update-workflow', {name: workflow.name}))
    } finally {
      this.savingWorkflow = false
    }
  }

  async onWorkflowChange(alias: string | null) {
    if (alias) await this.associateWorkflow(alias)
    else await this.removeWorkflow()
  }

  /**
   * Nada é buscado no servidor: a coleção, as irmãs e os fluxos já vêm no workspace.
   * O try/catch fica para o caso de a coleção da URL não existir.
   */
  async load() {
    try {
      if (!this.idea) throw new NotFoundError({collection: this.params.idea, filter: {}})
    } catch (err) {
      if (err instanceof NotFoundError) await this.$router.push({name: 'home'})
      else this.throwError(this.$tt('error.load-collection', {name: this.params.idea}), err)
    }
  }

  async deleteClass() {
    try {
        const ideaName = this.idea.name
        await this.deleteCurrentIdea()
        this.info(this.$tt('info.collection-deleted', {name: ideaName}))
        await this.$router.push(workspaceDesignRoute(this.params.system))
    } catch (err) {
      if (err instanceof DependencyError) {
        if (err.data.dependencies.instance) {
          const count = err.data.dependencies.instance
          //$tc e não $tt: a frase tem singular e plural
          this.throwError(this.$tc('error.delete-collection-instances', count, {count}))
        } else if (err.data.dependencies.ideaAlias) {
          this.throwError(this.$tt('error.delete-collection-linked', {collection: err.data.dependencies.ideaAlias}))
        } else {
          this.throwError(this.$tt('error.delete-collection-dependencies'))
        }
      }
      this.throwError(this.$tt('error.delete-collection', {name: this.idea.name}), err)
    }
  }

  async associateWorkflow(workflowAlias: string) {
    let workflow = this.workspace.workflowWith(workflowAlias)!;
    let workflowName = workflow.name;
    try {
      await this.updateCurrentIdea({workflow: workflowAlias})
      this.info(this.$tt('info.workflow-assigned', {name: workflowName}))
    } catch(err) {
      this.throwError(this.$tt('error.assign-workflow', {name: workflowName}), err)
    }
  }

  async removeWorkflow() {
    let workflowName = this.workflow!.name
    try {
      await this.updateCurrentIdea({workflow: null})
      this.info(this.$tt('info.workflow-unassigned', {name: workflowName}))
    } catch(err) {
      this.throwError(this.$tt('error.unassign-workflow', {name: workflowName}), err)
    }
  }
}

export default toNative(ViewClass)
</script>
<style scoped>
.access-formula {
  white-space: pre-wrap;
  font-size: 0.875rem;
}

/* As duas caixas de soltar, marcadas como tal (pedido do usuário): a das demais com o contorno de
   sempre; a do identificador em azul bem claro, de borda tracejada — o lugar especial, o do nome dos
   itens. Recusando o que se arrasta, a borda fica vermelha e a frase diz por quê. */
.property-zones {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Na tela larga (o `lg` do Vuetify), as duas caixas lado a lado, cada uma com metade (escolha do
   usuário): de lado a lado, cada propriedade ficaria com um vão no meio, e arrastar de uma caixa para
   a outra vira um gesto curto. As duas na altura da maior (pedido do usuário) — o `stretch` padrão da
   grade —, e a lista de cada uma estica até o fim dela: solta-se em qualquer ponto da caixa */
@media (min-width: 1280px) {
  .property-zones {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

/* O fundo das caixas aparece entre as plaquinhas das propriedades: um cinza bem leve na das demais, o
   azul na do identificador */
.property-zone {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 8px 0;
  background-color: rgba(var(--v-theme-on-surface), .03);
}

.property-zone--identifier {
  border: 1px dashed rgba(var(--v-theme-primary), .5);
  background-color: rgba(var(--v-theme-primary), .04);
}

.property-zone--refusing {
  border-color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), .04);
}

.property-zone-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 8px;
  padding: 0 8px 4px 12px;
}

/* O "+" na ponta direita do cabeçalho, como o da raia */
.property-zone-create {
  margin-left: auto;
}

.property-zone-title {
  font-weight: 500;
}

.property-zone-notice {
  margin: 0;
  padding: 0 12px 4px;
}

/* Espaço mesmo vazia: é onde se solta. As propriedades como plaquinhas empilhadas, com um respiro
   entre elas (escolha do usuário): com um fio de borda a borda, a caixa se cortava em faixas, e só a
   primeira parecia dela — as outras pareciam avulsas */
.property-list {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 48px;
  padding: 0 8px;
}
</style>
